import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Settings2,
  DollarSign,
  BarChart3,
  ArrowRight,
  CircleCheck,
  // Sell items
  User,
  Users,
  Target,
  MonitorPlay,
  FileText,
  // Operate items
  ShoppingCart,
  Palette,
  Factory,
  ClipboardList,
  Package,
  Truck,
  // Get Paid items
  Receipt,
  CreditCard,
  Calculator,
  Percent,
  // Grow items
  Store,
  UserSquare2,
  LineChart,
  Zap,
  Sparkles,
} from "lucide-react";
import { POWER_EVERY_PART_DEFAULT, mergeSection } from "@/data/homepageSectionDefaults";

// -----------------------------------------------------------------------------
// PowerEveryPart - Four-column feature pillar section for the homepage.
// Icons + per-pillar palette stay hardcoded and map to slots by position.
// Titles, taglines, feature-item labels, and the CTA banner are CMS-editable
// via block.data (see registry.js → power_every_part).
// -----------------------------------------------------------------------------

// Position-locked icon + theme metadata (4 slots, mirrors the 4 defaults).
const PILLAR_META = [
  {
    HeaderIcon: TrendingUp,
    itemIcons: [User, Users, Target, MonitorPlay, FileText],
    theme: {
      accent: "text-emerald-600",
      underline: "bg-emerald-500",
      headerBg: "bg-emerald-50",
      headerRing: "ring-emerald-100",
      itemBg: "bg-emerald-50/70",
      itemIcon: "text-emerald-600",
    },
  },
  {
    HeaderIcon: Settings2,
    itemIcons: [ShoppingCart, Palette, Factory, ClipboardList, Package, Truck],
    theme: {
      accent: "text-sky-600",
      underline: "bg-sky-500",
      headerBg: "bg-sky-50",
      headerRing: "ring-sky-100",
      itemBg: "bg-sky-50/70",
      itemIcon: "text-sky-600",
    },
  },
  {
    HeaderIcon: DollarSign,
    itemIcons: [Receipt, CreditCard, Calculator, Percent],
    theme: {
      accent: "text-amber-600",
      underline: "bg-amber-500",
      headerBg: "bg-amber-50",
      headerRing: "ring-amber-100",
      itemBg: "bg-amber-50/70",
      itemIcon: "text-amber-600",
    },
  },
  {
    HeaderIcon: BarChart3,
    itemIcons: [Store, UserSquare2, LineChart, Zap, Sparkles],
    theme: {
      accent: "text-violet-600",
      underline: "bg-violet-500",
      headerBg: "bg-violet-50",
      headerRing: "ring-violet-100",
      itemBg: "bg-violet-50/70",
      itemIcon: "text-violet-600",
    },
  },
];

function pillarKey(title, i) {
  return (title || `pillar-${i}`).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function PillarCard({ pillar, meta, delay }) {
  const key = pillarKey(pillar.title, 0);
  const { HeaderIcon, theme, itemIcons } = meta;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay }}
      className="relative bg-white rounded-2xl ring-1 ring-slate-100 shadow-sm px-5 py-6 sm:px-6 sm:py-7 h-full"
      data-testid={`power-pillar-${key}`}
    >
      {/* Header: icon bubble + title with colored underline */}
      <div className="flex items-center gap-3">
        <span className={`h-11 w-11 rounded-full ${theme.headerBg} ring-1 ${theme.headerRing} flex items-center justify-center`}>
          <HeaderIcon className={`h-5 w-5 ${theme.accent}`} strokeWidth={2.2} />
        </span>
        <div>
          <h3 className={`text-2xl font-extrabold ${theme.accent} leading-none`}>
            {pillar.title}
          </h3>
          <span className={`block mt-1.5 h-[3px] w-10 rounded-full ${theme.underline}`} aria-hidden />
        </div>
      </div>

      {/* Tagline */}
      <p className="mt-5 text-[14px] text-slate-600 leading-relaxed">
        {pillar.tagline}
      </p>

      {/* Feature items */}
      <ul className="mt-6 space-y-3">
        {(pillar.items || []).map((it, i) => {
          const Icon = itemIcons[i] || itemIcons[itemIcons.length - 1];
          const slug = (it.label || `item-${i}`).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
          return (
            <li
              key={`${it.label}-${i}`}
              className="flex items-center gap-3"
              data-testid={`power-pillar-${key}-item-${slug}`}
            >
              <span className={`shrink-0 h-8 w-8 rounded-lg ${theme.itemBg} flex items-center justify-center`}>
                <Icon className={`h-4 w-4 ${theme.itemIcon}`} strokeWidth={2} />
              </span>
              <span className="text-[14.5px] font-semibold text-slate-800">{it.label}</span>
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
}

export default function PowerEveryPart({ data }) {
  const c = mergeSection(POWER_EVERY_PART_DEFAULT, data);
  const pillars =
    Array.isArray(c.pillars) && c.pillars.length > 0
      ? c.pillars.slice(0, PILLAR_META.length)
      : POWER_EVERY_PART_DEFAULT.pillars;

  return (
    <section
      data-testid="power-every-part-section"
      className="relative bg-white py-16 sm:py-20 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.4 }}
          className="text-center text-xs sm:text-sm font-bold tracking-[0.24em] text-sky-600 uppercase"
          data-testid="power-eyebrow"
        >
          {c.eyebrow}
        </motion.p>

        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mt-3 text-center text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-900 leading-tight"
          data-testid="power-heading"
        >
          {c.heading}
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-4 max-w-3xl mx-auto text-center text-base sm:text-lg text-slate-600 leading-relaxed"
          data-testid="power-subtitle"
        >
          {c.subtitle}
        </motion.p>

        {/* Four-column pillar grid */}
        <div
          className="mt-12 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6"
          data-testid="power-pillar-grid"
        >
          {pillars.map((p, i) => (
            <PillarCard key={pillarKey(p.title, i)} pillar={p} meta={PILLAR_META[i] || PILLAR_META[PILLAR_META.length - 1]} delay={0.08 * i} />
          ))}
        </div>

        {/* CTA banner */}
        {(c.cta_label || c.cta_title) ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8 lg:mt-10 rounded-2xl bg-sky-50/70 ring-1 ring-sky-100 px-5 py-6 sm:px-8 sm:py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
            data-testid="power-cta-banner"
          >
            <div className="flex items-start sm:items-center gap-4">
              <span className="shrink-0 h-11 w-11 rounded-full bg-white ring-1 ring-sky-100 flex items-center justify-center">
                <CircleCheck className="h-5 w-5 text-sky-600" strokeWidth={2.2} />
              </span>
              <div>
                <p className="text-lg font-extrabold text-slate-900 leading-tight">
                  {c.cta_title}
                </p>
                <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                  {c.cta_subtitle}
                </p>
              </div>
            </div>
            {c.cta_label ? (
              <Link
                to={c.cta_href || "/solutions/advance"}
                data-testid="power-cta-explore-btn"
                className="btn-premium inline-flex items-center gap-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 text-sm font-bold shadow-lg shadow-sky-600/25 transition"
              >
                {c.cta_label}
                <ArrowRight className="h-4 w-4" />
              </Link>
            ) : null}
          </motion.div>
        ) : null}
      </div>
    </section>
  );
}
